import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { List, ListItem, ListItemButton, ListItemText, useTheme } from '@mui/material';
import { ScreenType } from '@tmdb-graphql-api/schema/types';
import { useState } from 'react';
import { MenuItems } from '#components/MainMenu/MenuItems.tsx';
import { MenuItemsQuery } from '#components/MainMenu/Query.tsx';
import { tt } from '#i18n/index.ts';

export type MainMenuProps = {
  setOpen: (open: boolean) => void;
};

type ExpandedScreenType = {
  MOVIE: boolean;
  TV: boolean;
};

export const MainMenu = ({ setOpen }: MainMenuProps) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState<ExpandedScreenType>({ MOVIE: true, TV: true });
  const bothExpanded = expanded[ScreenType.Movie] && expanded[ScreenType.Tv];
  const movieClosed = !expanded[ScreenType.Movie];
  const onlyMovieExpanded = expanded[ScreenType.Movie] && !expanded[ScreenType.Tv];
  const onlyTvExpanded = !expanded[ScreenType.Movie] && expanded[ScreenType.Tv];

  const moviePositionSx = {
    height: bothExpanded ? '50%' : onlyMovieExpanded ? 'calc(100% - 50px)' : '50px',
  };

  const tvPositionSx = {
    bottom: onlyMovieExpanded ? '0' : expanded[ScreenType.Tv] ? theme.spacing(2) : 'auto',
    height: bothExpanded ? '50%' : onlyTvExpanded ? 'auto' : '50px',
    top: bothExpanded ? '50%' : movieClosed ? '58px' : 'auto',
  };

  return (
    <List sx={{ color: theme.mainTextPrimary, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <ListItem
        disablePadding
        key="movies"
        sx={{
          flexDirection: 'column',
          overflow: 'hidden',
          paddingBottom: theme.spacing(1),
          paddingTop: 0,
          position: 'absolute',
          ...moviePositionSx,
        }}
      >
        <ListItemButton
          onClick={() => {
            setExpanded({ ...expanded, [ScreenType.Movie]: !expanded[ScreenType.Movie] });
          }}
          sx={{
            borderBottom: '1px solid',
            borderBottomColor: theme.supportingTextSecondary,
            boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2)',
            color: theme.supportingTextSecondary,
            flexGrow: 0,
            maxHeight: '49px',
            width: '100%',
          }}
        >
          <ListItemText primary={tt('screenTypes.MOVIE')} />
          {expanded[ScreenType.Movie] ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <MenuItemsQuery type={ScreenType.Movie}>
          {renderProps => (expanded[ScreenType.Movie] ? <MenuItems {...renderProps} setOpen={setOpen} /> : undefined)}
        </MenuItemsQuery>
      </ListItem>
      <ListItem
        disablePadding
        key="tv"
        sx={{
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'absolute',
          ...tvPositionSx,
        }}
      >
        <ListItemButton
          onClick={() => {
            setExpanded({ ...expanded, [ScreenType.Tv]: !expanded[ScreenType.Tv] });
          }}
          sx={{
            borderBottom: '1px solid',
            borderBottomColor: theme.supportingTextSecondary,
            boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2)',
            color: theme.supportingTextSecondary,
            flexGrow: 0,
            maxHeight: '49px',
            width: '100%',
          }}
        >
          <ListItemText primary={tt('screenTypes.TV')} />
          {expanded[ScreenType.Tv] ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <MenuItemsQuery type={ScreenType.Tv}>
          {renderProps => (expanded[ScreenType.Tv] ? <MenuItems {...renderProps} setOpen={setOpen} /> : undefined)}
        </MenuItemsQuery>
      </ListItem>
    </List>
  );
};
